import React, { useEffect } from "react";
import {
  BlockHeroCompact,
  BlockSection,
  BlockText,
  BlockFigure,
  RecipeRow,
  RecipeCell,
  RecipeBoard,
  projectPageShellClass,
  projectPageBottomClass,
  blockSectionBodyWrapClass,
  blockItalicSecondaryNoteClass,
} from "../../designSystem/projectBlocks";

const BASE = "/projects/APETools/images";

export default function APEToolsPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className="min-h-screen bg-white dark:bg-black text-black dark:text-white transition-colors duration-500">
      <div className={`${projectPageShellClass} pt-[72px] ${projectPageBottomClass} box-border`}>
        <BlockHeroCompact
          title="APE Tools"
          description="A set of semi-autonomous CAD tools built on the APE plugin, Grasshopper, and C# — each one collapses a complex parametric workflow into a single component, from voxelisation to fully fabricable facade systems."
          meta={["Frankfurt, Germany", "Rhenso", "Grasshopper", "C#", "APE", "2023"]}
        />

        <RecipeBoard>
          <RecipeRow>
            <RecipeCell span={12} className="!p-0 !border-r-0">
              <BlockFigure
                src={`${BASE}/APE concept.PNG`}
                alt="APE Tools — concept banner"
                ratio="banner"
                fit="cover"
              />
            </RecipeCell>
          </RecipeRow>
        </RecipeBoard>

        <BlockSection title="Purpose">
          <BlockText
            className={blockSectionBodyWrapClass}
            paragraphs={[
              "APE Tools are a set of semi-autonomous CAD tools developed for Rhenso, built on top of the APE plugin together with Grasshopper definitions and C# scripts. Each tool encapsulates a specific yet complex task — turning workflows that normally require a full parametric setup into a single component, and showing a new way of working with CAD systems.",
            ]}
          />
        </BlockSection>

        <BlockSection title="DELIFTA · parametric wall system">
          <BlockText
            className={blockSectionBodyWrapClass}
            paragraphs={[
              "Automates the entire process of designing a DELIFTA facade system (developed by Manni Green Tech) — every fabrication-level part is included, the system is fully adaptive, and a complete Bill of Materials is exported with the geometry.",
            ]}
          />

          <RecipeBoard>
            <RecipeRow>
              <RecipeCell span={6} className="!p-0">
                <BlockFigure src={`${BASE}/Delifta.PNG`} alt="DELIFTA tool" ratio="classic" fit="contain" caption="DELIFTA tool" />
              </RecipeCell>
              <RecipeCell span={6} className="!p-0 !border-r-0">
                <BlockFigure src={`${BASE}/Dlifta applied.PNG`} alt="DELIFTA applied to a facade" ratio="classic" fit="cover" caption="Applied result" />
              </RecipeCell>
            </RecipeRow>
          </RecipeBoard>
        </BlockSection>

        <BlockSection title="Voxelizer">
          <BlockText
            className={blockSectionBodyWrapClass}
            paragraphs={[
              "Turns any shape into an assembly of voxels — fast, parametric, and configurable for resolution and packing.",
            ]}
          />

          <RecipeBoard>
            <RecipeRow>
              <RecipeCell span={6} className="!p-0">
                <BlockFigure src={`${BASE}/Magica Voxel.PNG`} alt="Voxelizer — input shape" ratio="classic" fit="cover" caption="Source mesh" />
              </RecipeCell>
              <RecipeCell span={6} className="!p-0 !border-r-0">
                <BlockFigure src={`${BASE}/Rhino Viewport.PNG`} alt="Voxelizer — output assembly" ratio="classic" fit="cover" caption="Voxel output" />
              </RecipeCell>
            </RecipeRow>
          </RecipeBoard>
        </BlockSection>

        <BlockSection title="Adaptive wall shelves">
          <BlockText
            className={blockSectionBodyWrapClass}
            paragraphs={[
              "A dynamic grid tool for fully parametric, CNC-cut wall shelving. Connection details can be switched between CNC-milled and laser-cut variants, and the shelving adapts to any host surface.",
            ]}
          />

          <RecipeBoard>
            <RecipeRow>
              <RecipeCell span={6} className="!p-0">
                <BlockFigure src={`${BASE}/Dhelves.PNG`} alt="Adaptive wall shelves" ratio="classic" fit="contain" caption="Shelving tool" />
              </RecipeCell>
              <RecipeCell span={6} className="!p-0 !border-r-0">
                <BlockFigure src={`${BASE}/Shelves used.PNG`} alt="Adaptive shelves applied" ratio="classic" fit="cover" caption="Applied result" />
              </RecipeCell>
            </RecipeRow>
          </RecipeBoard>
        </BlockSection>

        <BlockSection title="3D Exploder">
          <BlockText
            className={blockSectionBodyWrapClass}
            paragraphs={[
              "Built on the Adaptive Parts Environment (APE), the 3D Exploder visualises complex assemblies by systematically separating their components — useful for assembly drawings, technical documentation, and animations.",
            ]}
          />

          <RecipeBoard>
            <RecipeRow>
              <RecipeCell span={6} className="!p-0">
                <BlockFigure src={`${BASE}/exploder.PNG`} alt="3D Exploder tool" ratio="classic" fit="contain" caption="Exploder tool" />
              </RecipeCell>
              <RecipeCell span={6} className="!p-0 !border-r-0">
                <BlockFigure src={`${BASE}/exploder used.PNG`} alt="3D Exploder applied to an assembly" ratio="classic" fit="cover" caption="Applied result" />
              </RecipeCell>
            </RecipeRow>
          </RecipeBoard>

          <p className={blockItalicSecondaryNoteClass}>
            Tool walkthroughs and additional details available on Rhenso’s site.
          </p>
        </BlockSection>
      </div>
    </main>
  );
}
